import { normalizeFocusSelection, getPrimaryPlanningIntent } from './planningIntent';

export const CONCEPT_FIRST = 'CONCEPT_FIRST';
export const DIRECT_CUSTOM = 'DIRECT_CUSTOM';
export const SHOPPING_ONLY = 'SHOPPING_ONLY';
export const QUICK_WORKFLOW = 'QUICK_WORKFLOW';
export const FULL_PLAN = 'FULL_PLAN';
export const THEME_IDEAS = 'THEME_IDEAS';

/**
 * Evaluates the planning intent and event parameters to decide on both the
 * current legacy routing behavior and the future recommended planning flow.
 *
 * @param {Object} eventData - Sanitized or raw event input state.
 * @returns {Object} Planning flow state metadata.
 */
export const getPlanningFlow = (eventData) => {
  const cleanEvent = eventData || {};
  const focusSelection = normalizeFocusSelection(cleanEvent.focus_selection);
  const primaryIntent = getPrimaryPlanningIntent(focusSelection);

  // Safely detect theme input state without mutating or altering eventData
  const themeInput = String(cleanEvent.theme_input || '').trim();
  let isSurpriseMe = false;
  let hasKnownTheme = false;

  if (themeInput) {
    const lowerInput = themeInput.toLowerCase();
    if (
      lowerInput.includes('surprise') ||
      lowerInput.includes('surprise me') ||
      themeInput === 'Theme Mode: Surprise Me'
    ) {
      isSurpriseMe = true;
    } else if (
      themeInput !== 'Theme Mode: Browsing...' &&
      themeInput !== 'Theme Mode: Custom Concept'
    ) {
      hasKnownTheme = true;
    }
  }

  const isThemeIdeas = primaryIntent === 'theme_ideas';
  const isPlanEverything = primaryIntent === 'everything';
  const isShoppingOnly = primaryIntent === 'shopping';
  const isFocusedOrMixed = !isThemeIdeas && !isPlanEverything;

  // 1. Compute Legacy/Current Behavior Mode
  let mode = FULL_PLAN;
  if (isThemeIdeas) {
    mode = THEME_IDEAS;
  } else if (isFocusedOrMixed) {
    mode = QUICK_WORKFLOW;
  }

  // 2. Compute Phase B/C/D Recommended Flow Mode
  let recommendedFutureMode = CONCEPT_FIRST;
  if (isThemeIdeas) {
    recommendedFutureMode = THEME_IDEAS;
  } else if (isPlanEverything) {
    recommendedFutureMode = CONCEPT_FIRST;
  } else if (isShoppingOnly) {
    recommendedFutureMode = SHOPPING_ONLY;
  } else if (isFocusedOrMixed) {
    if (isSurpriseMe || (!hasKnownTheme && themeInput === '')) {
      recommendedFutureMode = CONCEPT_FIRST;
    } else if (hasKnownTheme) {
      recommendedFutureMode = DIRECT_CUSTOM;
    } else {
      recommendedFutureMode = CONCEPT_FIRST; // Standard default fallback
    }
  }

  // Preserve quick-workflow detection logic for clean App.js integration
  const quickWorkflowMap = {
    decor: 'decor',
    food: 'food',
    activities: 'activities',
    shopping: 'shopping'
  };

  const shouldUseCurrentQuickWorkflow =
    focusSelection.length > 0 &&
    !focusSelection.includes('everything') &&
    !focusSelection.includes('theme_ideas') &&
    focusSelection.every(item => quickWorkflowMap[item]);

  let reason = '';
  if (isThemeIdeas) {
    reason = 'Theme Ideas intent generates concepts for user inspiration.';
  } else if (isFocusedOrMixed) {
    reason = 'Focused/mixed intents currently bypass LLM planning using database quick-workflow catalog filtering.';
  } else {
    reason = 'Plan Everything generates concept cards first and then the full event plan.';
  }

  return {
    mode,
    reason,
    focusSelection,
    primaryIntent,
    hasKnownTheme,
    isSurpriseMe,
    isThemeIdeas,
    isPlanEverything,
    isShoppingOnly,
    isFocusedOrMixed,
    shouldUseCurrentQuickWorkflow,
    recommendedFutureMode
  };
};
